<template>
  <v-card class="profitability-wrapper">
    <v-card-title class="text-center">Contract Profitability Report</v-card-title>
    <div class="content-container">
      <div class="export-div">
        <button class="btn btn-outline-primary" @click="
          csvExportClientProfitabilityDashboard(clientsProfitabilityDashboard)
          ">
          Download Table
        </button>
        <button class="btn btn-outline-primary ms-5" @click="clearFilterList()">
          Clear Filter
        </button>
        <h6 style="color: red" class="ms-5 ps-5 mt-1 pt-2">
          If "Months Into The Contract" is below 1 month we do not compute
        </h6>
      </div>

      <div v-if="!loading" class="d-flex justify-content-center align-items-center m-5">
        <div class="spinner-border text-dark" style="width: 7.5rem; height: 7.5rem" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else class="table-wrapper d-flex">
        <v-divider></v-divider>
        <div class="table-scroll d-flex">
          <v-divider></v-divider>
          <v-data-table-virtual :headers="headers" :items="clientsProfitabilityDashboardSorted" :search="searchQuery"
            fixed-header :loading="!loading" class="data-table">
            <template v-slot:item.CSTimeSpentVSAvailableFTECSM="{ value }">
              <v-chip :color="getColorForPercentageVS(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.PsTimeSpentVSAvailableFTE="{ value }">
              <v-chip :color="getColorForPercentageVS(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.ManDaysUsedVSAvailableTimeIntoContract="{
              value,
            }">
              <v-chip :color="getColorForPercentageVS(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.AverageMUVsVSEstimatedMUVs="{ value }">
              <v-chip :color="getColorForPercentageVS(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.ECOCSMTime="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.ECOPSTime="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.ECOMUVResourceUsage="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.ECOTotal="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.MonthsIntoTheContract="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template v-slot:item.TotalAnnualisedCostOverrun="{ value }">
              <v-chip :color="getColorForEco(value)">
                {{ value }}
              </v-chip>
            </template>
            <template #bottom></template>
          </v-data-table-virtual>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance, computed } from "vue";
import { useClientStore } from "../stores/client";
import _ from "lodash";

let contractProfitabilityProps = defineProps({
  sfClientId: {
    type: String,
    required: true,
  },
  clientId: {
    type: String,
    required: true,
  },
});

const clientStore = useClientStore();

const loading = ref(false);
const filterList = ref([]);
let originalClientsProfitabilityDashboard = [];
const clientsProfitabilityDashboardSorted = ref([]);
const instance = getCurrentInstance();
const Name = ref("");

const { contractProfitability } = storeToRefs(clientStore);

const {
  fetchContractProfitability,
  exportProfitalibityDashboard,
  csvExportClientProfitabilityDashboard,
} = clientStore;

const numberWithCommas = (x) => {
  if (x === undefined || x === null) {
    return "-";
  }

  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const getColorForPercentageVS = (value) => {
  let valInt = parseInt(value.split(" ")[0]);
  if (valInt >= 100) {
    return "red";
  } else if (valInt > 50) {
    return "orange";
  } else {
    return "green";
  }
};

const getColorForEco = (value) => {
  if (value !== "-") {
    return "blue";
  } else {
    return "none";
  }
};

const headers = [
  { title: "Client Name", key: "Name", fixed: true, width: "max-content" },
  { title: "CSM Name", key: "CSMName", width: "max-content" },
  { title: "Pod", key: "Pod", width: "max-content" },
  { title: "CSM Office", key: "CSMOffice", width: "max-content" },
  { title: "KAM Name", key: "KAMName", width: "max-content" },
  { title: "Start Date", key: "StartDate", width: "max-content" },
  { title: "End Date", key: "EndDate", width: "max-content" },
  { title: "Man-days Contracted", key: "ManDaysContracted", width: "max-content" },
  { title: "Man-days Contracted Per Month", key: "ManDaysContractedPerMonth", width: "max-content" },
  { title: "Contract Term", key: "ContractTerm", width: "max-content" },
  { title: "Months Left In Contract", key: "MonthsLeftInContract", width: "max-content" },
  { title: "Currency (local)", key: "Currency", width: "max-content" },
  {
    title: "License Total Price (Local)",
    key: "LicenseTotalPriceLocal",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.LicenseTotalPriceLocal)}`,
  },
  {
    title: "License Total Price (EUR)",
    key: "LicenseTotalPrice",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.LicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price",
    key: "TenPercentOfLicenseTotalPrice",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.TenPercentOfLicenseTotalPrice)}`,
  },
  {
    title: "10% Of License Total Price Per Month",
    key: "TenPercentOfLicenseTotalPricePerMonth",
    width: "max-content",
    value: (item) =>
      `${numberWithCommas(item.TenPercentOfLicenseTotalPricePerMonth)}`,
  },
  {
    title: "CS Plan Price (Local)",
    key: "CSPlanPriceLocal",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.CSPlanPriceLocal)}`,
  },
  {
    title: "CS Plan Price (EUR)",
    key: "CSPlanPrice",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.CSPlanPrice)}`,
  },
  {
    title: "Total CSM Budget",
    key: "TotalCSMBudget",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.TotalCSMBudget)}`,
  },
  {
    title: "Total CSM Budget Per Month",
    key: "TotalCSMBudgetPerMonth",
    width: "max-content",
    value: (item) => `${numberWithCommas(item.TotalCSMBudgetPerMonth)}`,
  },
  {
    title: "Available FTE CSM Per Month (days)",
    key: "AvailableFTECSMPerMonth",
    width: "max-content"
  },
  {
    title: "Available FTE CSM Since Beg Of Contract (days)",
    key: "AvailableFTECSMSinceContractStart",
    width: "max-content"
  },
  { title: "CS 10% Man-days Contracted", key: "CSTenPercentManDaysContracted", width: "max-content" },
  {
    title: "CS 10% Man-days Contracted Per Month",
    key: "CSTenPercentManDaysContractedPerMonth",
    width: "max-content"
  },
  {
    title: "CS Available 10% Man-days Contracted Since Beg Of Contract (days)",
    key: "CSTenPercentManDaysContractedSinceContractStart",
    width: "max-content"
  },
  {
    title: "CS Total Time Available Since Beg Of Contract",
    key: "CSTotalTimeAvailableSinceContractStart",
    width: "max-content"
  },
  {
    title: "CS Time Spent Since Beg Of Contract (days)",
    key: "CSTotalTimeSpentSince",
    width: "max-content"
  },
  {
    title: "CS Time Spent VS Available FTE",
    align: "end",
    key: "CSTimeSpentVSAvailableFTECSM",
    value: (item) => `${item.CSTimeSpentVSAvailableFTECSM} %`,
    width: "max-content"
  },
  {
    title: "PS 90% Man-days Contracted",
    key: "PSNinetyPercentManDaysContracted",
    width: "max-content"
  },
  {
    title: "PS 90% Man-days Contracted Per Months (days)",
    key: "PSNinetyPercentManDaysContractedPerMonth",
    width: "max-content"
  },
  {
    title: "PS Available 90% Man-days Contracted Since Beg Of Contract (days)",
    key: "PSNinetyPercentManDaysContractedSinceContractStart",
    width: "max-content"
  },
  {
    title: "PS Time Spent Since Beg Of Contract (days)",
    key: "PSTimeSpentSinceContractStart",
    width: "max-content"
  },
  {
    title: "PS Time Spent VS Available FTE",
    key: "PsTimeSpentVSAvailableFTE",
    value: (item) => `${item.PsTimeSpentVSAvailableFTE} %`,
    width: "max-content"
    },
  { title: "Man-days Used", key: "ManDaysUsed", width: "max-content" },
  {
    title: "Man-days Available Since Beg Of Contract",
    key: "ManDaysAvailableSinceContractStart",
    width: "max-content"
  },
  {
    title: "Man-days Used VS Available",
    key: "ManDaysUsedVSAvailableTimeIntoContract",
    value: (item) => `${item.ManDaysUsedVSAvailableTimeIntoContract} %`,
    width: "max-content"
  },
  { title: "Man-days Left", key: "ManDaysLeft", width: "max-content" },
  { title: "Man-days Left Per Months", key: "ManDaysLeftPerMonth", width: "max-content" },
  {
    title: "Estimated MUVs",
    key: "EstimatedMUVs",
    value: (item) => `${numberWithCommas(item.EstimatedMUVs)}`,
    width: "max-content"
  },
  {
    title: "UVs Since Opport Start Date (Client-side)",
    key: "UVsSinceLastOpportunity",
    value: (item) => `${numberWithCommas(item.UVsSinceLastOpportunity)}`,
    width: "max-content"
  },
  {
    title: "Average MUVs Since Opport Start Date",
    key: "AverageMUVsSinceLastOpportunity",
    value: (item) =>
      `${numberWithCommas(item.AverageMUVsSinceLastOpportunity)}`,
    width: "max-content"
  },
  {
    title: "Average MUVs VS Estimated MUVs",
    key: "AverageMUVsVSEstimatedMUVs",
    value: (item) => `${item.AverageMUVsVSEstimatedMUVs} %`,
    width: "max-content"
  },
  { title: "% Of UVs Used", key: "PercentageOfUVsUsed", width: "max-content" },
  {
    title: "% of Time Into The Contract",
    key: "PercentageIntoContract",
    value: (item) => `${item.PercentageIntoContract} %`,
    width: "max-content"
  },
  {
    title: "% of Time Into The Opportunity",
    key: "PercentageIntoTheOpportunity",
    value: (item) => `${item.PercentageIntoTheOpportunity} %`,
    width: "max-content"
  },
  { title: "SF ID", key: "SFAccountId", width: "max-content" },
  {
    title: "Estimated Cost Overrun",
    align: "center",
    children: [
      {
        title: "CS Time",
        key: "ECOCSMTime",
        value: (item) => {
          return item.ECOCSMTime > 0
            ? `${numberWithCommas(item.ECOCSMTime)}`
            : "-";
        },
        width: "max-content"
      },
      {
        title: "PS Time",
        key: "ECOPSTime",
        value: (item) => {
          return item.ECOPSTime > 0
            ? `${numberWithCommas(item.ECOPSTime)}`
            : "-";
        },
        width: "max-content"
      },
      {
        title: "MUV Resource Usage",
        key: "ECOMUVResourceUsage",
        value: (item) => {
          return item.ECOMUVResourceUsage > 0
            ? `${numberWithCommas(item.ECOMUVResourceUsage)}`
            : "-";
        },
        width: "max-content"
        },
      {
        title: "Total",
        key: "ECOTotal",
        value: (item) => {
          return item.ECOTotal > 0 ? `${numberWithCommas(item.ECOTotal)}` : "-";
        },
        width: "max-content"
        },
      { title: "Months Into The Contract", key: "MonthsIntoTheContract", width: "max-content" },
      {
        title: "Total Annualised Cost Overrun",
        key: "TotalAnnualisedCostOverrun",
        value: (item) => {
          return item.TotalAnnualisedCostOverrun > 0
            ? `${numberWithCommas(item.TotalAnnualisedCostOverrun)}`
            : "-";
        },
        width: "max-content"
      },
    ],
  },
];

const searchQuery = ref("");
let timeout;

watch(searchQuery, (newQuery) => {
  clearTimeout(timeout);
  timeout = setTimeout(() => { }, 500);
});

onMounted(async () => {
  await fetchContractProfitability(
    contractProfitabilityProps.sfClientId,
    contractProfitabilityProps.clientId,
  );
  originalClientsProfitabilityDashboard = [...contractProfitability.value];
  clientsProfitabilityDashboardSorted.value = [
    ...originalClientsProfitabilityDashboard,
  ];
  loading.value = true;
});
</script>

<script>
import Navbar from "../components/Navbar.vue";
import { storeToRefs } from "pinia";
export default {
  components: {
    navbar: Navbar,
  },
  methods: {},
};
</script>

<style lang="scss">
.profitability-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
}

.content-container {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.export-div {
  flex: 0 0 auto;
  margin-bottom: 1rem;
}

.table-wrapper {
  // flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow: auto;
  // background: white;
  // Remove any margin that might constrain width
  // margin: 0;
}

.table-scroll {
  width: max-content !important;
  min-width: 100%;
  // Scale down the table to fit more columns
  transform: scale(0.86);
  transform-origin: top left;
  margin-bottom: 20%;
}

.data-table {
  width: max-content !important;

  min-width: 100%;
}

// Ensure the Vuetify table doesn't have width constraints
:deep(.v-data-table) {
  width: max-content !important;
}

:deep(.v-data-table__wrapper) {
  width: max-content !important;
  overflow: auto !important;
}
</style>
